import React, { type ReactNode, useCallback } from 'react';
import {
  type GestureResponderEvent,
  StyleSheet,
  TouchableOpacity,
  type TouchableOpacityProps,
} from 'react-native';
import Animated from 'react-native-reanimated';

import { theme } from '@garnish/constants';

import { GlassView } from '../../../GlassView';
import { BodyText } from '../../../Text';
import { useNavbarPressScale } from '../../hooks/useNavbarPressScale';
import { NAVBAR_CONTENT_HEIGHT } from '../../Navbar.constants';

// ─────────────────────────────────────────────────────────────────────────────

export const NavbarButton = (props: Props) => {
  const {
    accessibilityRole = 'button',
    activeOpacity = NAVBAR_BUTTON_ACTIVE_OPACITY,
    children,
    isLiquidGlassEnabled = false,
    onPressIn,
    onPressOut,
    style,
    title,
    ...restProps
  } = props;

  const { handlePressIn, handlePressOut, pressScaleAnimatedStyles } = useNavbarPressScale();

  // ─── Handlers ────────────────────────────────────────────────────────

  const handleButtonPressIn = useCallback(
    (event: GestureResponderEvent) => {
      if (isLiquidGlassEnabled) handlePressIn();

      onPressIn?.(event);
    },
    [handlePressIn, isLiquidGlassEnabled, onPressIn],
  );

  const handleButtonPressOut = useCallback(
    (event: GestureResponderEvent) => {
      if (isLiquidGlassEnabled) handlePressOut();

      onPressOut?.(event);
    },
    [handlePressOut, isLiquidGlassEnabled, onPressOut],
  );

  // ─── Styles ──────────────────────────────────────────────────────────

  const buttonStyles = [
    styles.button,
    isLiquidGlassEnabled ? styles.buttonGlass : styles.buttonSolid,
    isLiquidGlassEnabled ? pressScaleAnimatedStyles : undefined,
    style,
  ];

  // ─────────────────────────────────────────────────────────────────────

  return (
    <AnimatedTouchableOpacity
      accessibilityRole={accessibilityRole}
      activeOpacity={activeOpacity}
      style={buttonStyles}
      onPressIn={handleButtonPressIn}
      onPressOut={handleButtonPressOut}
      {...restProps}
    >
      {isLiquidGlassEnabled ? <GlassView glassEffectStyle="clear" style={styles.glass} /> : null}

      <BodyText
        maxFontSizeMultiplier={1.2}
        numberOfLines={1}
        sizeMatch={['16']}
        style={styles.title}
      >
        {title}
      </BodyText>

      {children}
    </AnimatedTouchableOpacity>
  );
};

// ─── Animated Components ─────────────────────────────────────────────────────

const AnimatedTouchableOpacity = Animated.createAnimatedComponent(TouchableOpacity);

// ─── Constants ───────────────────────────────────────────────────────────────

const NAVBAR_BUTTON_MAX_WIDTH = 100;
const NAVBAR_BUTTON_ACTIVE_OPACITY = 0.9;

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  button: {
    width: '100%',
    maxWidth: NAVBAR_BUTTON_MAX_WIDTH,
    height: NAVBAR_CONTENT_HEIGHT,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: theme.radius['3xLarge'],
    paddingHorizontal: theme.spacing['4'],
  },
  buttonSolid: {
    backgroundColor: theme.colors.KALE,
  },
  buttonGlass: {
    backgroundColor: 'rgba(0, 71, 60, 0.95)',
  },
  glass: {
    ...StyleSheet.absoluteFill,
    borderRadius: theme.radius['3xLarge'],
    pointerEvents: 'none',
  },
  title: {
    color: theme.colors.WHITE,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = Omit<TouchableOpacityProps, 'children'> & {
  children?: ReactNode;
  isLiquidGlassEnabled?: boolean;
  title: ReactNode;
};
